import { faMagnifyingGlassPlus } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { arrayOf, bool, func, number, string } from 'prop-types'
import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { debounce } from 'lodash'
import { theme } from 'globalStyles'

export const Slider = ({ images, currentIndex, fullScreen, setFullScreen }) => {
  const listRef = useRef(null)
  const wrapRef = useRef(null)
  const [left, setLeft] = useState(0)
  const calculateLeft = useCallback(() => {
    if (currentIndex === 0 || !listRef.current) return 0

    return -listRef.current.children[currentIndex].offsetLeft
  }, [currentIndex])
  const leftRef = useRef({ left, calculateLeft })
  useEffect(() => {
    leftRef.current = { left, calculateLeft }
  }, [left, calculateLeft])

  const [zoomIconPosition, setZoomIconPosition] = useState({ r: 0, t: 0 })
  const imageRef = useRef(null)
  const setZoomItemPosition = useCallback(() => {
    if (fullScreen || !imageRef.current) return
    const r = (wrapRef.current.offsetWidth - imageRef.current.offsetWidth) / 32 + 1
    const t = (wrapRef.current.offsetHeight - imageRef.current.offsetHeight) / 32 + 1

    setZoomIconPosition({ r, t })
  }, [fullScreen])
  useEffect(() => {
    setZoomItemPosition()
    const fixZoomItem = debounce(setZoomItemPosition)

    window.addEventListener('resize', fixZoomItem)

    return () => window.removeEventListener('resize', fixZoomItem)
  }, [setZoomItemPosition])

  useEffect(() => {
    setLeft(calculateLeft())
  }, [calculateLeft])

  useEffect(() => {
    const { left, calculateLeft } = leftRef.current
    if (left === calculateLeft()) return listRef.current.removeAttribute('style')

    listRef.current.style.transition = '0s'
    listRef.current.style.left = calculateLeft() + 'px'
    setLeft(calculateLeft())
    listRef.current.removeAttribute('style')
  }, [fullScreen])

  return (
    <Wrap fullScreen={fullScreen} ref={wrapRef}>
      {!fullScreen && (
        <ZoomIcon icon={faMagnifyingGlassPlus} {...zoomIconPosition} onClick={() => setFullScreen(true)} />
      )}
      <List ref={listRef} left={left}>
        {images.map((image, index) => (
          <Item key={index} fullScreen>
            <img src={image} ref={index === 0 ? imageRef : undefined} onLoad={setZoomItemPosition} />
          </Item>
        ))}
      </List>
    </Wrap>
  )
}

Slider.propTypes = {
  images: arrayOf(string).isRequired,
  currentIndex: number.isRequired,
  fullScreen: bool,
  setFullScreen: func,
}

const Wrap = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  background-color: transparent;

  ${({ fullScreen }) =>
    fullScreen &&
    `
      position: fixed;
      width: 100%!important;
      height: 100%;
      left: 0;
      top: 0;
      background-color: rgba(255, 255, 255, 0.9);
      z-index: 100;
    `}

  ${theme.breakpoints.tablet} {
    width: 486px;
  }
`
const List = styled.ul`
  white-space: nowrap;
  position: relative;
  transition: 0.4s;
  left: ${({ left }) => left}px;
  font-size: 0rem;
  height: 100%;
`

const Item = styled.li`
  display: inline-flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;

  & > img {
    max-width: 100%;
    max-height: 100%;
    ${({ fullScreen }) =>
      fullScreen &&
      `
      max-width: 80%;
      max-height: 80%;
    `}
  }
`
const ZoomIcon = styled(FontAwesomeIcon)`
  color: var(--color-primary);
  position: absolute;
  font-size: 2rem;
  top: ${({ t }) => t}rem;
  right: ${({ r }) => r}rem;
  z-index: 2;
  cursor: pointer;
`
